import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import Navigation from '../components/Navigation';
import ProfileCard, { DiscoverProfile, ReviewStep } from '../components/ProfileCard';
import GoodVibesPrompt from '../components/GoodVibesPrompt';

const Discover: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<DiscoverProfile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [step, setStep] = useState<ReviewStep>('photo');
  const [voiceLiked, setVoiceLiked] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [autoPlay, setAutoPlay] = useState(true);
  const [blocked, setBlocked] = useState<{ reason: string; match_id?: string; active_count?: number } | null>(null);
  const [matchDialog, setMatchDialog] = useState<{ match_id: string; match_status: string } | null>(null);
  const [modelInfo, setModelInfo] = useState<{ has_model: boolean; interaction_count: number } | null>(null);
  const [goodVibesPrompt, setGoodVibesPrompt] = useState<{
    targetUserId: string;
    targetName?: string;
    categories: Record<string, string>;
  } | null>(null);

  const fetchBatch = useCallback(async () => {
    setLoading(true);
    setError('');
    setBlocked(null);
    try {
      const response = await api.get('/api/discover/batch?count=10');
      if (response.data.blocked) {
        setBlocked({
          reason: response.data.reason,
          match_id: response.data.match_id,
          active_count: response.data.active_count,
        });
        setProfiles([]);
      } else {
        setProfiles(response.data.profiles);
      }
      if (response.data.has_model !== undefined) {
        setModelInfo({
          has_model: response.data.has_model,
          interaction_count: response.data.interaction_count ?? 0,
        });
      }
      setCurrentIndex(0);
      setStep(autoPlay ? 'voice' : 'photo');
      setVoiceLiked(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [autoPlay]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  const currentProfile = profiles[currentIndex] || null;

  const nextProfile = () => {
    const nextIdx = currentIndex + 1;
    if (nextIdx < profiles.length) {
      setCurrentIndex(nextIdx);
      setStep(autoPlay ? 'voice' : 'photo');
      setVoiceLiked(null);
    } else {
      fetchBatch();
    }
  };

  const handleListenComplete = () => {
    if (step === 'photo') setStep('voice');
    else if (step === 'voice') setStep('voice_feedback');
  };

  const handleVoiceFeedback = (liked: boolean) => {
    setVoiceLiked(liked);
    setStep('action');
  };

  const handleLike = async () => {
    if (!currentProfile) return;
    setSending(true);
    setError('');
    try {
      const res = await api.post(`/api/discover/${currentProfile.user_id}/interact`, {
        action: 'like',
        voice_liked: voiceLiked,
      });
      if (res.data.is_match) {
        setMatchDialog({ match_id: res.data.match_id, match_status: res.data.match_status });
      } else {
        nextProfile();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record interaction');
    } finally {
      setSending(false);
    }
  };

  const handleSkip = async () => {
    if (!currentProfile) return;
    const skippedProfile = currentProfile;
    setSending(true);
    setError('');
    try {
      await api.post(`/api/discover/${skippedProfile.user_id}/interact`, {
        action: 'skip',
        voice_liked: voiceLiked,
      });
      try {
        const res = await api.get(`/api/compliments/prompt-check?target_user_id=${skippedProfile.user_id}`);
        if (res.data.show_prompt) {
          setGoodVibesPrompt({
            targetUserId: skippedProfile.user_id,
            targetName: skippedProfile.tags?.[0],
            categories: res.data.categories,
          });
        }
      } catch {}
      nextProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record interaction');
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async (userId: string) => {
    try { await api.post('/api/profile/block', { target_user_id: userId }); nextProfile(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Failed to block user'); }
  };

  const handleReport = async (userId: string, reason: string) => {
    try { await api.post('/api/profile/report', { target_user_id: userId, reason }); nextProfile(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Failed to submit report'); }
  };

  const handleSendVoice = async (audioBlob: Blob) => {
    if (!currentProfile) return;
    setSending(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('voice', new File([audioBlob], 'message.webm', { type: 'audio/webm' }));
      await api.post(`/api/discover/${currentProfile.user_id}/voice-message?voice_liked=${voiceLiked}`, formData);
      nextProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send voice message');
      throw err;
    } finally {
      setSending(false);
    }
  };

  const isProfileComplete = !!(user?.photo_url && user?.voice_recording_url && user?.location);
  if (!isProfileComplete) return <Navigate to="/profile" replace />;

  return (
    <>
      <Navigation />
      <Container maxWidth="sm" sx={{ mt: 2, mb: 3 }}>
        {/* Header row: title + learning icon | auto-play */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Typography variant="caption" sx={{ color: "primary.main" }}>
              Discover
            </Typography>
            {modelInfo && !blocked && !loading && (
              modelInfo.has_model ? (
                <Tooltip title="Profiles ranked by your preferences" arrow>
                  <AutoAwesomeIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                </Tooltip>
              ) : (
                <Tooltip title="Still learning — profiles shown randomly" arrow>
                  <SchoolIcon sx={{ fontSize: 12, color: 'info.main' }} />
                </Tooltip>
              )
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentProfile && !blocked && !loading && (
              <FormControlLabel
                control={<Switch checked={autoPlay} onChange={(e) => setAutoPlay(e.target.checked)} size="small" />}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <VolumeUpOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: "0.6rem", color: "text.disabled" }}>Auto-play</Typography>
                </Box>}
                sx={{ m: 0 }}
              />
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress size={20} sx={{ color: "primary.main" }} />
          </Box>
        )}

        {/* Blocked states */}
        {!loading && blocked && blocked.reason === 'at_capacity' && (
          <BlockedCard
            title="5 active conversations"
            body="End a conversation to discover new people."
            action="Go to Conversations"
            onClick={() => navigate('/conversations')}
          />
        )}

        {!loading && blocked && blocked.reason === 'pending_feedback' && (
          <BlockedCard
            title="Feedback needed"
            body="Complete your farewell before discovering new people."
            action="Complete Feedback"
            onClick={() => navigate(`/conversations/${blocked.match_id}/feedback`)}
          />
        )}

        {!loading && blocked && blocked.reason === 'not_launched' && (
          <BlockedCard
            title="Pre-launch"
            body="Your profile is on the waitlist. We'll text you when we go live."
            action="Complete Profile"
            onClick={() => navigate('/profile')}
          />
        )}

        {!loading && !blocked && profiles.length === 0 && (
          <BlockedCard
            title="No new profiles"
            body="Check back later — new people join every day."
          />
        )}

        {/* Profile card */}
        {!loading && !blocked && currentProfile && (
          <ProfileCard
            profile={currentProfile}
            step={step}
            onListenComplete={handleListenComplete}
            onVoiceFeedback={handleVoiceFeedback}
            onLike={handleLike}
            onSkip={handleSkip}
            onSendVoice={handleSendVoice}
            onBlock={handleBlock}
            onReport={handleReport}
            sending={sending}
            learning={modelInfo ? !modelInfo.has_model : false}
          />
        )}

        {goodVibesPrompt && (
          <GoodVibesPrompt
            open={!!goodVibesPrompt}
            onClose={() => setGoodVibesPrompt(null)}
            targetUserId={goodVibesPrompt.targetUserId}
            targetName={goodVibesPrompt.targetName}
            categories={goodVibesPrompt.categories}
          />
        )}

        {/* Match dialog */}
        <Dialog open={!!matchDialog} PaperProps={{ sx: { textAlign: 'center', p: 2 } }}>
          <DialogTitle>
            <FavoriteIcon sx={{ fontSize: 24, color: "primary.main", display: 'block', mx: 'auto', mb: 1 }} />
            <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: '1.1rem' }}>
              It's a match
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {matchDialog?.match_status === 'active'
                ? 'You can now start a conversation.'
                : 'Both at capacity. This match is pending until a slot opens.'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
            {matchDialog?.match_status === 'active' && (
              <Button variant="contained" size="small" onClick={() => { navigate(`/conversations/${matchDialog?.match_id}`); setMatchDialog(null); }}>
                Go to Conversation
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => { setMatchDialog(null); nextProfile(); }}>
              Keep Discovering
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

function BlockedCard({ title, body, action, onClick }: { title: string; body: string; action?: string; onClick?: () => void }) {
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: '1rem', color: 'text.primary', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: action ? 2 : 0, maxWidth: 320, mx: 'auto' }}>
        {body}
      </Typography>
      {action && onClick && (
        <Button variant="outlined" size="small" onClick={onClick}>
          {action}
        </Button>
      )}
    </Box>
  );
}

export default Discover;
